import React from 'react';

// TJ's founder story, supplied 07/09/2026. Verbatim - do not rewrite,
// paraphrase or trim without his approval. The wording is deliberate: it
// keeps EFAM in body-sculpting territory and out of rehab/medical claims.
const PARAGRAPHS = [
  "I didn't build EFAM because fitness was easy for me. I built it because, at one point, it wasn't.",
  'For most of my life, training was something I understood. Push yourself. Be disciplined. Work harder. Improve.',
  'Then injuries changed the rules. My body stopped letting me train the way I was used to. Things that had once been simple became difficult, painful or sometimes impossible.',
  'And that was probably the hardest part. It wasn’t just losing the ability to train normally. It was losing something that had always given me confidence, structure and momentum.',
  'I still wanted to get stronger. I still wanted to improve. I still wanted to feel like myself. I just needed another way to do it.',
  'That search is what eventually led me to EMS technology. What interested me wasn’t a promise of an easy shortcut. It was the possibility of creating meaningful muscle activation and training stimulus without relying entirely on the heavy loads and traditional training methods my body was struggling with.',
  'That changed the way I looked at fitness. I realised there are a lot of people who still want to become stronger, healthier and more confident, but traditional training does not always fit where their body, lifestyle or circumstances are right now.',
  'Maybe they are coming back from injury. Maybe they have limited time. Maybe high-impact training does not suit them. Maybe they are intimidated by gyms. Maybe they simply want another tool.',
  'That is why I created EFAM: Easy Fitness & Massage.',
  'Not to replace effort. Not to promise miracles. And definitely not to pretend a machine does all the work for you.',
  'EFAM is about making better training options easier to access. It is about combining technology, education, support and intelligent progression so people can keep moving forward.',
  'The machines became one part of that idea. The bigger idea was always this: meet people where they are, then help them move forward from there.',
  'That philosophy now runs through everything I want EFAM to become. Every machine. Every session. Every piece of education. Every new service. And every improvement we make.',
  'Because I know what it feels like when your body changes the plan you had for yourself. But changing the plan does not mean giving up on the goal. Sometimes progress means finding a different way forward.',
  'That is what EFAM was built to do.'
];

const FounderStory = () => {
  return (
    <section
      id="founder-story"
      className="py-24 lg:py-32 bg-gradient-to-b from-black via-slate-950 to-black relative overflow-hidden"
      aria-labelledby="founder-story-heading"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent"></div>

      <div className="container mx-auto px-6 lg:px-16 relative z-10">
        <h2
          id="founder-story-heading"
          className="text-sm uppercase tracking-[0.3em] text-amber-500 font-semibold mb-12 text-center"
          data-testid="founder-story-heading"
        >
          Why EFAM exists
        </h2>

        <div className="max-w-5xl mx-auto grid gap-10 lg:gap-14 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)] lg:items-start">
          {/* Portrait */}
          <figure className="mx-auto w-full max-w-[280px] sm:max-w-[320px] lg:sticky lg:top-28 lg:mx-0">
            <div className="relative rounded-2xl overflow-hidden border border-amber-500/25 shadow-2xl shadow-black/60">
              <picture>
                <source
                  type="image/webp"
                  srcSet="/images/founder/tj-founder.webp 800w, /images/founder/tj-founder@2x.webp 1200w"
                  sizes="(min-width: 1024px) 320px, 280px"
                />
                <img
                  src="/images/founder/tj-founder.jpg"
                  alt="Tim Holt, founder of EFAM Fitness"
                  width="800"
                  height="1067"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-auto block"
                />
              </picture>
              <div
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 to-transparent"
              ></div>
            </div>
            <figcaption className="mt-4 text-center lg:text-left">
              <span className="block text-white font-semibold">Tim Holt</span>
              <span className="block text-slate-500 text-sm">Founder, EFAM Fitness</span>
            </figcaption>
          </figure>

          {/* Story */}
          <div>
            <div
              className="space-y-5 text-slate-300 text-base sm:text-lg leading-relaxed"
              data-testid="founder-story-body"
            >
              {PARAGRAPHS.map((text, i) => (
                <p key={i}>{text}</p>
              ))}
            </div>

            <p className="mt-10 text-amber-400 font-semibold tracking-wide" data-testid="founder-story-signature">
              Always learning. Ever evolving.
              <span className="block text-slate-400 font-normal mt-1">
                You are your own greatest project.
              </span>
            </p>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-700/50 to-transparent"></div>
    </section>
  );
};

export default FounderStory;
